import React, { useState, useMemo } from 'react';
import CreationModal from '../../components/ui/CreationModal';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CustomSnackbar from '../../components/ui/Snackbar';
import { transactionAPI } from '../../services/api';

const TransactionEditModal = ({ open, onClose, onSubmit, loading = false, transaction = null, descriptionOnly = false }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formData, setFormData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const hasRefs = !descriptionOnly && transaction?.references?.length > 0;

  const fields = useMemo(() => {
    const f = [
      {
        name: 'description',
        type: 'textarea',
        placeholder: 'Description',
        label: 'DESCRIPTION',
        required: false
      }
    ];
    if (hasRefs) {
      f.push({
        name: 'codeClient',
        type: 'text',
        placeholder: 'Code client',
        label: 'CODE CLIENT',
        required: false
      });
      f.push({
        name: 'bc',
        type: 'text',
        placeholder: 'Numéro de bon de caisse',
        label: 'BON DE CAISSE',
        required: false
      });
    }
    return f;
  }, [hasRefs]);

  const initialData = useMemo(() => {
    const data = { description: transaction?.description || '' };
    if (hasRefs) {
      data.bc = transaction?.references?.find(r => r.type === 'BC')?.value || '';
      data.codeClient = transaction?.references?.find(r => r.type === 'CODE_CLIENT')?.value || '';
    }
    return data;
  }, [transaction, hasRefs]);

  const handleSubmit = (data) => {
    const originalDescription = transaction?.description || '';
    const originalBc = transaction?.references?.find(r => r.type === 'BC')?.value || '';
    const originalCc = transaction?.references?.find(r => r.type === 'CODE_CLIENT')?.value || '';

    const descriptionChanged = (data.description?.trim() || '') !== originalDescription;
    const refsChanged = hasRefs && (
      (data.bc?.trim() || '') !== originalBc ||
      (data.codeClient?.trim() || '') !== originalCc
    );

    if (!descriptionChanged && !refsChanged) {
      onClose();
      return;
    }
    setFormData(data);
    setShowConfirmDialog(true);
  };

  const confirmEdit = async () => {
    if (!formData || !transaction) return;
    try {
      const originalDescription = transaction?.description || '';
      const descriptionChanged = (formData.description?.trim() || '') !== originalDescription;

      if (descriptionChanged) {
        await transactionAPI.updateDescription(transaction.id, { description: formData.description?.trim() || '' });
      }

      let updatedRefs = transaction.references;
      if (hasRefs) {
        const originalBc = transaction?.references?.find(r => r.type === 'BC')?.value || '';
        const originalCc = transaction?.references?.find(r => r.type === 'CODE_CLIENT')?.value || '';
        const refsChanged =
          (formData.bc?.trim() || '') !== originalBc ||
          (formData.codeClient?.trim() || '') !== originalCc;

        if (refsChanged) {
          const refs = [];
          if (formData.bc?.trim()) refs.push({ type: 'BC', value: formData.bc.trim() });
          if (formData.codeClient?.trim()) refs.push({ type: 'CODE_CLIENT', value: formData.codeClient.trim() });
          await transactionAPI.updateRefs(transaction.id, refs);
          updatedRefs = refs;
        }
      }

      onSubmit(transaction.id, formData.description?.trim() || '', updatedRefs);
      setShowConfirmDialog(false);
      setFormData(null);
      onClose();
    } catch (error) {
      console.error('Error updating transaction:', error);
      setShowConfirmDialog(false);
      setSnackbar({ open: true, message: 'Erreur lors de la modification', severity: 'error' });
    }
  };

  const handleClose = () => {
    setFormData(null);
    onClose();
  };

  return (
    <>
      <CreationModal
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        title="Modifier la transaction"
        fields={fields}
        submitButtonText="Enregistrer"
        loading={loading}
        initialData={initialData}
        showLabels={true}
        fieldsPerRow={1}
      />

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={confirmEdit}
        title="Confirmer la modification"
        message="Êtes-vous sûr de vouloir modifier cette transaction ?"
        confirmText="Enregistrer"
        cancelText="Annuler"
        loading={loading}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default TransactionEditModal;
